import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Search, BookOpen, LayoutGrid, ShieldCheck, Quote } from "lucide-react";
import { Button } from "../components/ui/button";

const heroImg = "https://customer-assets.emergentagent.com/job_speech-therapy-aec/artifacts/cr1xlef7_IMG_2416.webp";
const bubblesImg = "https://images.unsplash.com/photo-1777308889022-34802f0599a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxidWJibGVzJTIwc2Vuc29yeSUyMHBsYXl8ZW58MHx8fHwxNzc3NjE4NzU0fDA&ixlib=rb-4.1.0&q=85";
const blocksImg = "https://images.unsplash.com/photo-1760113762872-631f32c7fab0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMGJ1aWxkaW5nJTIwYmxvY2tzJTIwcGxheXxlbnwwfHx8fDE3Nzc2MTg3ODB8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const nav = useNavigate();

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="relative organic-bg overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E6F0E9] text-[#2C4C3B] text-xs font-semibold tracking-[0.15em] uppercase" data-testid="hero-eyebrow">
              <Sparkles className="w-3.5 h-3.5" /> CommPass · by Sensiiq
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#2C4C3B] leading-[1.05]">
              The <span className="text-[#C66A51]">only global,</span><br />
              <span className="text-[#2C4C3B]">SLP-built</span> AAC programming<br />
              and goal-setting platform.
            </h1>
            <p className="mt-6 text-lg text-[#52606D] max-w-xl leading-relaxed">
              A clinical decision-support tool for SLPs, educators, and AAC specialists to generate
              personalised AAC programming word lists and SMART goal banks based on an individual's
              profile, AAC communicator stage, and diversity-specific severity or stage.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button
                className="btn-primary rounded-full px-7 py-6 text-base"
                onClick={() => nav("/search")}
                data-testid="hero-cta-start"
              >
                Start searching
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Link
                to="/about"
                className="px-6 py-3 rounded-full text-[#2C4C3B] hover:bg-[#E6F0E9] text-sm font-medium"
                data-testid="hero-cta-about"
              >
                Learn more →
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-[#52606D]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2C4C3B]" /> Clinician-built</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C66A51]" /> AI-assisted, evidence-aligned</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-[#A8BAA3]/30 blur-2xl" />
              <div className="relative rounded-3xl shadow-xl overflow-hidden w-full h-[540px]">
                <img
                  src={heroImg}
                  alt="Young AAC user with communication device"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 22%" }}
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-4 shadow-lg border border-[#E2E8F0] w-64">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-[#C66A51]" />
                  <span className="text-xs uppercase tracking-[0.15em] text-[#52606D]">Stage 3</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {["big bubble", "pop bubble", "my bubble", "blue bubble", "more bubble"].map((w, i) => (
                    <span key={i} className={`px-2.5 py-1 rounded-full text-xs ${i === 2 ? "fringe-badge" : "core-badge"}`}>{w}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#C66A51]">How it works</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium text-[#2C4C3B]">
            Three clicks from activity to device-ready word list.
          </h2>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {[
            { icon: LayoutGrid, title: "1. Pick an activity", body: "Choose from bubbles, books, vehicles, play-doh, puzzles and more — the bread and butter of therapy sessions." },
            { icon: Search, title: "2. Select differences profile & stage", body: "Twenty+ differences profiles × six stages — from single words to full narrative discourse." },
            { icon: Sparkles, title: "3. Get 50 curated words", body: "Core-fringe tagged suggestions tailored to the client, ready to programme into the AAC grid." },
          ].map((step, i) => (
            <div key={i} className="card-lift p-8 rounded-2xl bg-white border border-[#E2E8F0]/60" data-testid={`how-step-${i+1}`}>
              <div className="w-11 h-11 rounded-xl bg-[#E6F0E9] flex items-center justify-center text-[#2C4C3B] mb-5">
                <step.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl font-medium text-[#2C4C3B]">{step.title}</h3>
              <p className="mt-2 text-[#52606D] leading-relaxed text-sm">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Activities showcase */}
      <section className="bg-[#F4F5F0] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-xl">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#C66A51]">Activities</span>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium text-[#2C4C3B]">
                Built around the toys therapists actually use.
              </h2>
            </div>
            <Link to="/search" className="text-[#2C4C3B] text-sm font-medium hover:underline">Browse all activities →</Link>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="relative rounded-3xl overflow-hidden h-72 card-lift">
              <img src={bubblesImg} alt="Bubbles activity" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C4C3B]/80 to-transparent" />
              <div className="absolute bottom-0 p-6 text-white">
                <span className="text-xs tracking-[0.2em] uppercase opacity-80">Sensory • Early Language</span>
                <h3 className="font-display text-2xl mt-1">Bubbles</h3>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden h-72 card-lift">
              <img src={blocksImg} alt="Building play activity" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C4C3B]/80 to-transparent" />
              <div className="absolute bottom-0 p-6 text-white">
                <span className="text-xs tracking-[0.2em] uppercase opacity-80">Cognitive • Spatial</span>
                <h3 className="font-display text-2xl mt-1">Puzzles & Blocks</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About credit */}
      <section className="max-w-5xl mx-auto px-6 py-24 text-center">
        <Quote className="w-10 h-10 text-[#C66A51] mx-auto" />
        <p className="mt-6 font-display text-2xl sm:text-3xl text-[#2C4C3B] leading-snug">
          "Every child, every adult, deserves a voice. This tool was built to help therapists
          programme that voice — thoughtfully, quickly, and stage-by-stage."
        </p>
        <p className="mt-6 text-sm tracking-[0.2em] uppercase text-[#52606D]">
          — Sadiya Yasin, Speech-Language Pathologist
        </p>
        <p className="mt-1 text-xs text-[#C66A51]">A product of Sensiiq</p>
      </section>
    </div>
  );
}
